import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch, type SubmitHandler } from "react-hook-form";
import { z } from "zod";

import { dateString } from "@/lib/forms/schema";

import { customCaseTypesApi } from "@/features/customCaseTypes/api";
import {
  caseCustomType,
  cleanTypeName,
  customTypeIdOf,
  customTypeValue,
  MAX_TYPE_NAME
} from "@/features/customCaseTypes/customCaseTypes";

import { MattersAPI } from "../../../../api/matters";
import type {
  CreateForeignNationalMatterRequest,
  MatterDTO
} from "../../../../api/types/matter";
import { VISAVALUE } from "../../../../types/visa.type";
import {
  NOIR_NOID_WORKFLOW_OPTION,
  RFE_WORKFLOW_OPTION,
  WorkflowType
} from "../../../../types/workflow.type";
import { dateInputToIso, toDateInputValue } from "../../lib/format";

export type CreateCaseValues = {
  /** Base visa, or the RFE / NOID-NOIR workflow option. */
  workflow: string | null;
  /** The underlying visa when `workflow` is RFE / NOID-NOIR. */
  noticeVisa: string | null;
  filingDeadline: string;
  assignedUserIds: string[];
  /** A Custom Workflow case's name ("EB-1A + Consular"); blank is unnamed. */
  customName: string;
  /** Save the name as a firm custom case type (owners and admins). */
  saveForFirm: boolean;
};

const EMPTY: CreateCaseValues = {
  workflow: null,
  noticeVisa: null,
  filingDeadline: "",
  assignedUserIds: [],
  customName: "",
  saveForFirm: false
};

/** Form values for an existing case (onboarding's "change case type"). */
export const valuesFromMatter = (matter: MatterDTO): CreateCaseValues => {
  const caseVisa = (matter.case?.visa ?? "").toString().trim() || null;
  const ind = (matter.individual ?? {}) as Record<string, unknown>;
  const workflowType = ind.workflow_type as WorkflowType | undefined;
  const isRfe = Boolean(ind.is_rfe);

  const custom = caseCustomType(matter.case);
  let workflow: string | null = custom?.id
    ? customTypeValue(custom.id)
    : caseVisa;
  let noticeVisa: string | null = null;
  if (workflowType === WorkflowType.NOIR_NOID) {
    workflow = NOIR_NOID_WORKFLOW_OPTION;
    noticeVisa = caseVisa;
  } else if (isRfe) {
    workflow = RFE_WORKFLOW_OPTION;
    noticeVisa = caseVisa;
  }

  return {
    workflow,
    noticeVisa,
    filingDeadline: toDateInputValue(matter.case?.filing_deadline),
    assignedUserIds: (matter.assigned_to_user_ids ?? []).filter(Boolean),
    customName: custom && !custom.id ? custom.name : "",
    saveForFirm: false
  };
};

/** Custom Workflow picked as is, so the case can be named. */
export const isUnnamedCustomWorkflow = (workflow: string | null): boolean =>
  workflow === VISAVALUE.CUSTOM_TEMPLATE;

export const isNoticeWorkflow = (workflow: string | null): boolean =>
  workflow === RFE_WORKFLOW_OPTION || workflow === NOIR_NOID_WORKFLOW_OPTION;

export const noticeWorkflowLabel = (workflow: string | null): string =>
  workflow === NOIR_NOID_WORKFLOW_OPTION ? "NOID/NOIR" : "RFE";

/** A workflow, plus the underlying visa when the workflow is a notice. */
export const createCaseSchema: z.ZodType<CreateCaseValues, CreateCaseValues> = z
  .object({
    workflow: z
      .string()
      .nullable()
      .refine((v) => Boolean(v), "Visa workflow is required."),
    noticeVisa: z.string().nullable(),
    filingDeadline: dateString(),
    assignedUserIds: z.array(z.string()),
    customName: z
      .string()
      .refine(
        (v) => cleanTypeName(v).length <= MAX_TYPE_NAME,
        `Keep the name to ${MAX_TYPE_NAME} characters.`
      ),
    saveForFirm: z.boolean()
  })
  .superRefine((v, ctx) => {
    if (
      isUnnamedCustomWorkflow(v.workflow) &&
      v.saveForFirm &&
      !cleanTypeName(v.customName)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Name it to save it for the firm.",
        path: ["customName"]
      });
    }
    if (isNoticeWorkflow(v.workflow) && !v.noticeVisa) {
      ctx.addIssue({
        code: "custom",
        message: `Visa type is required for ${noticeWorkflowLabel(
          v.workflow
        )}.`,
        path: ["noticeVisa"]
      });
    }
  });

/**
 * Request body for the values; null when the form is incomplete. A saved
 * custom case type (picked, or `savedTypeId` just saved from the name) is a
 * Custom Workflow case of that type; a name alone is a one-off.
 */
export const buildCreateCasePayload = (
  values: CreateCaseValues,
  currentUserId: string,
  savedTypeId: string | null = null
): CreateForeignNationalMatterRequest | null => {
  const notice = isNoticeWorkflow(values.workflow);
  const typeId = customTypeIdOf(values.workflow) ?? savedTypeId;
  const visa = typeId
    ? VISAVALUE.CUSTOM_TEMPLATE
    : notice
    ? values.noticeVisa
    : values.workflow;
  if (!visa || visa === VISAVALUE.EMPTY) return null;
  const oneOffName =
    !typeId && isUnnamedCustomWorkflow(values.workflow)
      ? cleanTypeName(values.customName)
      : "";

  const assigned =
    values.assignedUserIds.length > 0
      ? values.assignedUserIds
      : currentUserId.trim()
      ? [currentUserId.trim()]
      : [];
  const deadline = dateInputToIso(values.filingDeadline);

  return {
    case: {
      visa: visa as VISAVALUE,
      ...(notice ? { is_rfe: true } : {}),
      workflow_type:
        values.workflow === NOIR_NOID_WORKFLOW_OPTION
          ? WorkflowType.NOIR_NOID
          : values.workflow === RFE_WORKFLOW_OPTION
          ? WorkflowType.RFE
          : WorkflowType.STANDARD,
      ...(deadline ? { filing_deadline: deadline } : {}),
      ...(typeId ? { custom_case_type_id: typeId } : {}),
      ...(oneOffName ? { custom_case_type_name: oneOffName } : {})
    },
    ...(assigned.length > 0 ? { assigned_to_user_ids: assigned } : {})
  };
};

type Options = {
  open: boolean;
  clientId: string;
  currentUserId: string;
  initialMatter: MatterDTO | null;
  onCreated: (matter: MatterDTO) => void;
  /** Where "Save for the Firm" saves the name. */
  lawFirmId?: string | null;
  /** After a name is saved as a firm custom case type. */
  onTypeSaved?: () => void;
};

export const useCreateCaseForm = ({
  open,
  clientId,
  currentUserId,
  initialMatter,
  onCreated,
  lawFirmId = null,
  onTypeSaved
}: Options) => {
  const form = useForm<CreateCaseValues>({
    resolver: zodResolver(createCaseSchema),
    defaultValues: EMPTY
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // (Re)hydrate whenever the sheet opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setIsSaving(false);
    form.reset(
      initialMatter
        ? valuesFromMatter(initialMatter)
        : {
            ...EMPTY,
            assignedUserIds: currentUserId.trim() ? [currentUserId.trim()] : []
          }
    );
  }, [currentUserId, form, initialMatter, open]);

  const isEditing = Boolean(initialMatter);
  const workflow = useWatch({ control: form.control, name: "workflow" });
  const notice = isNoticeWorkflow(workflow);
  const nameable = isUnnamedCustomWorkflow(workflow);

  /** Changing the workflow invalidates any notice visa chosen for the old one. */
  const setWorkflow = useCallback(
    (next: string | null) => {
      setError(null);
      form.setValue("workflow", next, {
        shouldDirty: true,
        shouldValidate: form.formState.submitCount > 0
      });
      form.setValue("noticeVisa", null, { shouldDirty: true });
      form.clearErrors("noticeVisa");
      if (!isUnnamedCustomWorkflow(next)) {
        form.setValue("customName", "", { shouldDirty: true });
        form.setValue("saveForFirm", false, { shouldDirty: true });
        form.clearErrors("customName");
      }
    },
    [form]
  );

  const submit = useCallback<SubmitHandler<CreateCaseValues>>(
    async (values) => {
      if (!buildCreateCasePayload(values, currentUserId)) return;
      setIsSaving(true);
      setError(null);
      try {
        let savedTypeId: string | null = null;
        if (
          isUnnamedCustomWorkflow(values.workflow) &&
          values.saveForFirm &&
          lawFirmId
        ) {
          const saved = await customCaseTypesApi.create(lawFirmId, {
            name: cleanTypeName(values.customName)
          });
          savedTypeId = saved.id;
          form.setValue("workflow", customTypeValue(saved.id));
          form.setValue("saveForFirm", false);
          onTypeSaved?.();
        }
        const payload = buildCreateCasePayload(
          values,
          currentUserId,
          savedTypeId
        );
        if (!payload) return;
        const created = await MattersAPI.createForForeignNational(
          clientId,
          payload
        );
        onCreated(created);
      } catch (e) {
        setError(
          e instanceof Error
            ? e.message
            : "Failed to create case. Please try again."
        );
      } finally {
        setIsSaving(false);
      }
    },
    [clientId, currentUserId, form, lawFirmId, onCreated, onTypeSaved]
  );

  return useMemo(
    () => ({
      form,
      isEditing,
      notice,
      nameable,
      workflow,
      isSaving,
      error,
      setWorkflow,
      submit
    }),
    [
      error,
      form,
      isEditing,
      isSaving,
      nameable,
      notice,
      setWorkflow,
      submit,
      workflow
    ]
  );
};

export type CreateCaseForm = ReturnType<typeof useCreateCaseForm>;
